import React, { useState, useEffect } from "react";
import {
  Clock, CheckCircle, XCircle, FileText
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card } from "../components/ui/card";
import SharedHeader4 from "../Headers/SharedHeader4";
import SharedFooter3 from "../Footer/SharedFooter3";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { useSelector } from "react-redux";
import { fetchQuotesByUserIdWithFilters } from "../redux/Slices/quoteSlice";
import { PurchaseQuoteTypeEnum, QuoteStatusEnum } from "../modules/shared/model/quote.enum";
import PurchasedQuoteListing from "../components/ui/PurchasedQuoteListing";

const ProposalsDashboard = () => {
  // Redux hooks
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: any) => state.auth);
  const quotesState = useAppSelector((state: any) => state.quotes);

  // Get stats directly from Redux state (populated by PurchasedQuoteListing)
  const totalProposalsCount = quotesState?.totalCount || 0;
  const countsLoading = quotesState?.loading || false;

  // Calculate stats from the quotes in Redux state
  const stats = React.useMemo(() => {
    const quotes = quotesState?.userQuotes || [];
    
    // Count different statuses
    const usedCount = quotes.filter((q: any) => 
      q.status?.id === QuoteStatusEnum.Used
    ).length;
    
    const activeCount = quotes.filter((q: any) => 
      q.status?.id === QuoteStatusEnum.Active
    ).length;
    
    const inactiveCount = quotes.filter((q: any) => 
      q.status?.id === QuoteStatusEnum.InActive
    ).length;

    return {
      totalProposals: totalProposalsCount,
      usedProposals: usedCount,
      activeProposals: activeCount,
      inactiveProposals: inactiveCount
    };
  }, [quotesState?.userQuotes, totalProposalsCount]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col"  style={{ textAlign: "left" }}>
      <SharedHeader4 />
      
      <main className="flex-1 max-w-7xl mx-auto px-4 py-8 w-full">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t("myProposals.title")}</h1>
          <p className="text-gray-600 mt-1">{t("myProposals.subtitle")}</p>
        </div>

        {/* Proposal Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <span className="text-sm text-gray-500">{t("myProposals.totalQuotes")}</span>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {countsLoading ? "..." : stats.totalProposals}
              </h3>
              <p className="text-sm text-gray-600">{t("myProposals.assignedQuotes")}</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-500">{t("myProposals.used")}</span>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {countsLoading ? "..." : stats.usedProposals}
              </h3>
              <p className="text-sm text-gray-600">{t("myProposals.usedQuotes")}</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <span className="text-sm text-gray-500">{t("myProposals.active")}</span>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {countsLoading ? "..." : stats.activeProposals}
              </h3>
              <p className="text-sm text-gray-600">{t("myProposals.activeQuotes")}</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <span className="text-sm text-gray-500">{t("myProposals.inactive")}</span>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {countsLoading ? "..." : stats.inactiveProposals}
              </h3>
              <p className="text-sm text-gray-600">{t("myProposals.inactiveQuotes")}</p>
            </div>
          </Card>
        </div>

        {/* Active Proposals */}
        <Card className="mb-8">
          <div className="px-4 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">{t("myProposals.activeProposals")}</h2>
            <p className="text-sm text-gray-600 mt-1">{t("myProposals.purchasedQuotesDescription")}</p>
          </div>

          {/* Proposals List - Using PurchasedQuoteListing Component */}
          {/* Show only Assign type quotes (purchaseTypeId = 3) */}
          <PurchasedQuoteListing userId={user?.id} purchaseTypeIds="3" />
        </Card>
      </main>

      {/* Footer Actions - Mobile Only */}
      <SharedFooter3/>
    </div>
  );
};

export default ProposalsDashboard;